import * as React from 'react';
import { cn } from 'lib/utils';

export interface StatusTabOption<T extends string> {
  value: T;
  label: string;
  /** Items in this status; leave undefined while unknown. */
  count?: number;
}

interface StatusTabsProps<T extends string> {
  options: StatusTabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  'aria-label'?: string;
}

/**
 * A list's main status as underline tabs with counts (All · Draft · Active …).
 * The current tab is ink — a thin lime line would not read on white. Arrow
 * keys, Home and End move between tabs.
 */
export function StatusTabs<T extends string>({
  options,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: StatusTabsProps<T>) {
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = options.length - 1;
    const target = (() => {
      switch (event.key) {
        case 'ArrowRight':
          return index === last ? 0 : index + 1;
        case 'ArrowLeft':
          return index === 0 ? last : index - 1;
        case 'Home':
          return 0;
        case 'End':
          return last;
        default:
          return -1;
      }
    })();
    if (target < 0) return;
    event.preventDefault();
    tabRefs.current[target]?.focus();
    if (options[target].value !== value) onChange(options[target].value);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('no-scrollbar flex items-stretch gap-6 overflow-x-auto border-b border-border', className)}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => {
              if (!active) onChange(option.value);
            }}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              '-mb-px inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 text-[13.5px] transition-colors',
              active
                ? 'border-foreground font-semibold text-foreground'
                : 'border-transparent font-medium text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
            {option.count != null && (
              <span
                className={cn(
                  'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11.5px] font-medium tabular-nums',
                  active ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground',
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
